import { useEffect } from 'react';

/**
 * Hook that triggers the provided callback when a click event occurs outside the ref element.
 *
 * @param ref - The ref to the element to watch for clicks outside of.
 * @param callback - The callback to be called when a click event occurs outside the ref element.
 * @template T - The type of the element being watched.
 */
export const useClickOutside = <T extends HTMLElement>(
    ref: React.RefObject<T>,
    callback: (event?: MouseEvent) => void,
) => {
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                callback(event);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, callback]);
};
